import styled, { css, keyframes } from 'styled-components';

import { ANIM, COLORS, REDUCED_MOTION } from '@/nova/theme/tokens';
import { Text } from '@/nova/components';

/** Commission-plan section inside the affiliate drill-down (its own top-bordered block, like SitesSection). */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const SectionLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.heading};
`;

/** The affiliate's current assignment(s). */
export const CurrentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const CurrentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${COLORS.surfaceMuted};
`;

export const PlanMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PlanName = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const PlanDesc = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** The create-and-assign form, in its own recessed inset so it reads as a distinct block separated
 *  from the current-assignment list above it. Uses the shared "inset inside a card" token
 *  (metricTileBg): a recess that renders DARKER than the sheet surface behind it, so the box sits
 *  back and its lighter input fields / chips stand out. (surfaceMuted here was a light white overlay,
 *  which washed the contents out against the modal.) */
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  padding: 14px;
  border-radius: 12px;
  background: ${COLORS.metricTileBg};
  border: 1px solid ${COLORS.hairline};
`;

export const TierList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TierRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

export const TierFields = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/** Wraps an action button without boxing it (`display: contents`): while `$busy`, the button's glyph turns as a
 *  spinner, until the server has answered and the section has re-read the plan (still, under reduced motion). */
export const BusyAction = styled.div<{ $busy: boolean }>`
  display: contents;

  ${(p) =>
    p.$busy &&
    css`
      svg {
        animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;
      }

      ${REDUCED_MOTION} {
        svg {
          animation: none;
        }
      }
    `}
`;
